import { useState } from 'react';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useVersions } from '@/services/queries';
import { useAppSelector } from '@/store/hooks';
import { canRelease, type VersionListQuery } from '@/types';
import { Badge } from '@/shared/ui/badge';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { ErrorState, Pagination } from '@/components/common';
import { formatBytes, formatDateTime } from '@/shared/lib/format';
import { parseByteSize } from '@/lib/format';
import { PublishVersionDialog } from './PublishVersionDialog';

const PAGE_SIZE = 20;
const ANY = 'any';

/**
 * Published builds.
 *
 * Code signing gets a column of its own rather than being folded into a details
 * pane. It is the single property most likely to make a release fail on a
 * customer's machine, and the failure arrives as a support ticket days later.
 */
const PLATFORM_LABEL: Record<string, string> = {
  windows: 'Windows',
  linux: 'Linux',
  macos: 'macOS',
};

export function VersionTable() {
  const [channel, setChannel] = useState<string>(ANY);
  const [offset, setOffset] = useState(0);

  const role = useAppSelector((state) => state.auth.user?.role);
  const mayPublish = canRelease(role);

  const query: VersionListQuery = {
    limit: PAGE_SIZE,
    offset,
    ...(channel !== ANY ? { channel: channel as VersionListQuery['channel'] } : {}),
  };

  const versions = useVersions(query);
  const COLUMNS = 8;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={channel}
          onValueChange={(value) => {
            setChannel(value);
            setOffset(0);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Filter by channel">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All channels</SelectItem>
            <SelectItem value="stable">Stable</SelectItem>
            <SelectItem value="beta">Beta</SelectItem>
          </SelectContent>
        </Select>

        {/*
          Disabled rather than hidden for anyone who cannot publish. A missing
          button reads as a broken page; a disabled one with a reason reads as a
          permission. The server refuses either way - this only saves somebody
          filling in a long form to be told no at the end.
        */}
        {mayPublish ? (
          <PublishVersionDialog />
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="ml-auto">
                <Button disabled>Publish version</Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Publishing requires superadmin or engineering.</TooltipContent>
          </Tooltip>
        )}
      </div>

      {versions.isError && (
        <ErrorState
          title="Could not load versions"
          description={versions.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void versions.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Version</TableHead>
              <TableHead className="w-24">Channel</TableHead>
              <TableHead className="w-24">Platform</TableHead>
              <TableHead className="w-32">Signed</TableHead>
              <TableHead className="hidden md:table-cell w-28">Size</TableHead>
              <TableHead className="hidden lg:table-cell">Notes</TableHead>
              <TableHead className="hidden md:table-cell w-28">Min supported</TableHead>
              <TableHead className="w-44">Published</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versions.isPending &&
              Array.from({ length: 5 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={COLUMNS}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {versions.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS} className="py-10 text-center text-muted-foreground">
                  Nothing published on this channel yet.
                </TableCell>
              </TableRow>
            )}

            {versions.data?.data.map((version) => (
              <TableRow key={version.id}>
                <TableCell className="font-medium tabular-nums">
                  {version.versionNumber}
                  {version.mandatory && (
                    <Badge className="ml-2 font-normal text-xs">Mandatory</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <StatusPill status={version.releaseChannel} minWidth={64} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {PLATFORM_LABEL[version.platform] ?? version.platform}
                </TableCell>
                <TableCell>
                  {version.codeSigned ? (
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <ShieldCheck className="size-4" /> Signed
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm text-destructive">
                      <ShieldAlert className="size-4" /> Unsigned
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground tabular-nums">
                  {formatBytes(parseByteSize(version.fileSizeBytes))}
                </TableCell>
                <TableCell className="hidden lg:table-cell max-w-sm">
                  <span className="line-clamp-2 text-sm text-muted-foreground">
                    {version.releaseNotes ?? '—'}
                  </span>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground tabular-nums">
                  {version.minSupportedVersion ?? '—'}
                </TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {formatDateTime(version.publishedAt)}
                  {/* CI registers builds with its release key, not as a person. */}
                  {version.publishedBy === null && <span className="block text-xs">by CI</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      {versions.data && (
        <Pagination
          offset={offset}
          pageSize={PAGE_SIZE}
          count={versions.data.data.length}
          total={versions.data.meta.total}
          hasMore={versions.data.meta.hasMore}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}
